import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AlertTriangle, Check, Loader2, PenLine, Play, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { API, apiError } from "@/lib/api";

const V2 = `${API}/pipeline-v2`;
const SEV = { critical: "text-rose-200 bg-rose-950/50 border-rose-800/50", major: "text-amber-200 bg-amber-950/40 border-amber-800/50", minor: "text-slate-300 bg-slate-800/50 border-slate-700/60" };
const STATUS = { open: "Needs decision", unverified: "Unverified", accepted: "Accepted", fixed: "Fixed", rewrite_pending: "Rewrite queued" };
const FILTERS = [["open", "Needs decision / unverified"], ["rewrite_pending", "Queued"], ["resolved", "Resolved"], ["all", "All"]];
const label = (t) => (t || "").replace(/_/g, " ");
const field = "w-full bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2 font-ui text-sm text-slate-100 outline-none focus:border-gold-400/60";

const context = (book, f) => {
  const text = book?.chapters?.[f.chapter - 1]?.text || "";
  const q = (f.quote || "").slice(0, 60);
  if (!q) return "";
  return text.split("\n\n").find((p) => p.includes(q)) || "";
};

const FlagCard = ({ f, book, onAct }) => {
  const [mode, setMode] = useState("");
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const para = context(book, f);
  const actionable = f.status === "open" || f.status === "unverified";
  const submit = async () => {
    setBusy(true);
    try { await onAct(f.id, mode, mode === "accept" ? { reason: txt } : { instruction: txt }); setMode(""); setTxt(""); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`flag-card-${f.id}`} className="rounded-xl border border-gold-500/12 bg-navy-900/40 p-4">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className={`font-ui text-[10px] uppercase tracking-widest rounded-full border px-2 py-0.5 ${SEV[f.severity] || SEV.minor}`}>{f.severity}</span>
        <span className="font-ui text-sm font-semibold text-slate-100 capitalize">{label(f.type)}</span>
        <span className="font-ui text-xs text-slate-400">{f.chapter ? `Chapter ${f.chapter}` : "Whole book"} · {f.origin === "code" ? "Rule check" : "GPT reviewer"}</span>
        <span data-testid={`flag-status-${f.id}`} className="ml-auto font-ui text-xs text-gold-200">{STATUS[f.status] || f.status}</span>
      </div>
      {f.quote && <p className="font-read text-sm text-slate-200 italic border-l-2 border-gold-500/40 pl-3 mb-2">“{f.quote}”</p>}
      {para && para.trim() !== f.quote.trim() && (
        <details className="mb-2"><summary className="font-ui text-xs text-gold-300 cursor-pointer" data-testid={`flag-context-${f.id}`}>Show passage in context</summary>
          <p className="font-read text-sm text-slate-400 mt-2 whitespace-pre-line">{para}</p></details>
      )}
      <p className="font-ui text-sm text-slate-300"><span className="text-slate-500">Why: </span>{f.evidence}</p>
      {f.fix && <p className="font-ui text-sm text-slate-300 mt-1"><span className="text-slate-500">Suggested fix: </span>{f.fix}</p>}
      {f.status === "unverified" && <p className="font-ui text-xs text-amber-300 mt-1">The quoted text could not be found in the chapter. This may be a false alarm.</p>}
      {f.note && <p className="font-ui text-xs text-amber-300 mt-1">{f.note}</p>}
      {f.reason && <p className="font-ui text-xs text-emerald-300 mt-1">Accepted: {f.reason}</p>}
      {f.status === "rewrite_pending" && <p className="font-ui text-xs text-sky-300 mt-1">Rewrite queued{f.instruction ? `: ${f.instruction}` : ""}. It runs on the next pipeline run.</p>}
      {f.fixed_by_version != null && <p className="font-ui text-xs text-emerald-300 mt-1">Fixed in chapter version v{f.fixed_by_version}</p>}
      {actionable && !mode && (
        <div className="flex gap-2 mt-3">
          <button data-testid={`flag-accept-${f.id}`} onClick={() => setMode("accept")} className="flex items-center gap-1 font-ui text-sm rounded-lg px-3 py-1.5 border border-emerald-700/50 text-emerald-200 hover:border-emerald-500 transition-colors"><Check className="w-4 h-4" /> Accept as is</button>
          <button data-testid={`flag-rewrite-${f.id}`} onClick={() => setMode("rewrite")} className="flex items-center gap-1 font-ui text-sm rounded-lg px-3 py-1.5 border border-gold-500/30 text-gold-200 hover:border-gold-400/70 transition-colors"><PenLine className="w-4 h-4" /> Rewrite</button>
        </div>
      )}
      {mode && (
        <div className="mt-3 space-y-2">
          <textarea data-testid={`flag-input-${f.id}`} rows={2} value={txt} onChange={(e) => setTxt(e.target.value)} className={field}
            placeholder={mode === "accept" ? "Why is this fine? (required, saved with the book)" : "Optional instruction for the rewrite, e.g. keep the scar on her left hand"} />
          <div className="flex gap-2">
            <button data-testid={`flag-confirm-${f.id}`} onClick={submit} disabled={busy || (mode === "accept" && !txt.trim())}
              className="klyr-btn-metal disabled:opacity-50 font-ui text-sm font-semibold rounded-lg px-4 py-1.5">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "accept" ? "Confirm accept" : "Queue rewrite"}</button>
            <button data-testid={`flag-cancel-${f.id}`} onClick={() => { setMode(""); setTxt(""); }} className="font-ui text-sm text-slate-400 hover:text-slate-200 px-2">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

const Summary = ({ book }) => {
  const fs = book.scan?.findings || [];
  const n = (s) => fs.filter((f) => f.status === s).length;
  const cells = [["Needs decision", n("open"), "text-amber-200"], ["Unverified", n("unverified"), "text-slate-300"], ["Queued", n("rewrite_pending"), "text-sky-200"], ["Fixed", n("fixed"), "text-emerald-200"], ["Accepted", n("accepted"), "text-slate-200"]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5" data-testid="flags-summary">
      {cells.map(([l, v, c]) => (
        <div key={l} className="rounded-xl border border-gold-500/12 bg-navy-900/40 px-4 py-3">
          <div className={`font-display text-2xl ${c}`} data-testid={`flags-count-${l.toLowerCase().replace(/ /g, "-")}`}>{v}</div>
          <div className="font-ui text-xs text-slate-400">{l}</div>
        </div>
      ))}
    </div>
  );
};

export const ContinuityFlags = () => {
  const [books, setBooks] = useState(null);
  const [sel, setSel] = useState("");
  const [book, setBook] = useState(null);
  const [filter, setFilter] = useState("open");
  const [running, setRunning] = useState(false);

  const loadBooks = () => axios.get(`${V2}/books`).then((r) => {
    setBooks(r.data);
    const first = r.data.find((b) => b.scan) || r.data[0];
    if (!sel && first) setSel(first.id);
  }).catch((e) => toast.error(apiError(e)));
  const loadBook = (id) => axios.get(`${V2}/books/${id}`).then((r) => setBook(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { loadBooks(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (sel) loadBook(sel); }, [sel]);

  const act = async (fid, action, body) => {
    try {
      const r = await axios.post(`${V2}/books/${sel}/scan/${fid}/action`, { action, ...body });
      setBook(r.data);
      toast.success(action === "accept" ? "Flag accepted" : "Rewrite queued");
    } catch (e) { toast.error(apiError(e)); }
  };
  const runRewrites = async () => {
    if (!window.confirm("This resumes the pipeline and makes PAID AI calls (Claude rewrite + GPT re-review) for each queued rewrite. Continue?")) return;
    setRunning(true);
    try { await axios.post(`${V2}/books/${sel}/run`); toast.success("Pipeline resumed"); } catch (e) { toast.error(apiError(e)); } finally { setRunning(false); }
  };

  const findings = useMemo(() => {
    const fs = book?.scan?.findings || [];
    if (filter === "all") return fs;
    if (filter === "open") return fs.filter((f) => f.status === "open" || f.status === "unverified");
    if (filter === "resolved") return fs.filter((f) => f.status === "fixed" || f.status === "accepted");
    return fs.filter((f) => f.status === filter);
  }, [book, filter]);
  const queued = (book?.scan?.findings || []).some((f) => f.status === "rewrite_pending");

  if (!books) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-slate-400 animate-spin" /></div>;
  if (!books.length) return <p className="font-read text-slate-400" data-testid="flags-empty">No Pipeline V2 books yet.</p>;
  return (
    <div className="max-w-4xl" data-testid="continuity-flags-panel">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <select data-testid="flags-book-select" value={sel} onChange={(e) => setSel(e.target.value)} className={`${field} max-w-sm`}>
          {books.map((b) => <option key={b.id} value={b.id}>{b.premise?.title || b.id} {b.scan ? `· ${(b.scan.findings || []).filter((f) => f.status === "open").length} need a decision` : "· not scanned"}</option>)}
        </select>
        <button data-testid="flags-refresh" onClick={() => { loadBooks(); sel && loadBook(sel); }} className="p-2 rounded-lg border border-gold-500/12 text-slate-300 hover:border-gold-400/60 transition-colors"><RefreshCw className="w-4 h-4" /></button>
        {queued && (
          <button data-testid="flags-run-rewrites" onClick={runRewrites} disabled={running} className="ml-auto flex items-center gap-1 klyr-btn-metal disabled:opacity-50 font-ui text-sm font-semibold rounded-lg px-4 py-2">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Apply queued rewrites
          </button>
        )}
      </div>
      {!book ? <Loader2 className="w-5 h-5 text-slate-400 animate-spin" /> : !book.scan ? (
        <p className="font-read text-slate-400" data-testid="flags-not-scanned">This book has not had a full-draft continuity scan yet. The scan runs automatically after the draft.</p>
      ) : (
        <>
          {book.scan.report?.summary && <p className="font-read text-slate-300 mb-4 flex items-center gap-2" data-testid="flags-report-summary"><ShieldCheck className="w-4 h-4 text-gold-300" />{book.scan.report.summary}</p>}
          <Summary book={book} />
          <div className="flex gap-2 mb-4">
            {FILTERS.map(([k, l]) => (
              <button key={k} data-testid={`flags-filter-${k}`} onClick={() => setFilter(k)}
                className={`font-ui text-xs font-medium rounded-full px-4 py-1.5 transition-colors ${filter === k ? "bg-gold-400 text-stone-950" : "text-slate-300 border border-gold-500/12 hover:border-gold-400/60"}`}>{l}</button>
            ))}
          </div>
          <div className="space-y-3">
            {findings.length ? findings.map((f) => <FlagCard key={f.id} f={f} book={book} onAct={act} />) : (
              <p className="font-read text-slate-400 flex items-center gap-2" data-testid="flags-none"><AlertTriangle className="w-4 h-4" /> Nothing here.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};
